import { useDebounce } from '@reactuses/core';
import { ChevronLeft, StethoscopeIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import type { GetCurrentUser } from '@virtual-care/client';
import { getLocalizedSpeciality } from '@virtual-care/isomorphic';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@virtual-care/shadcn';
import { HomeSectionHeader, Loading, useData } from '@virtual-care/ui';

import { useLanguage } from '../../../i18n/use-language.ts';
import {
  Header,
  ProfileCard,
  SearchComponent,
} from '../../components/header/index.ts';
import { PriceLine } from '../../components/priceline.tsx';
import { SpecialtyCard } from '../../components/specialty-card.tsx';
import { useUser } from '../../firebase.tsx';
import { useSetting } from '../../hooks/use-settings.tsx';
import { PrintingRequestCard } from '../printing/printing-request-card.tsx';
import { AppointmentCard } from './appointment-card.tsx';
import { HomecareReservationCard } from './homecare-reservation-card.tsx';
import { SecondOpinionCard } from './second-opinion-card.tsx';

type QuotaPool = 'consultation' | 'printing' | 'none';

const OPTIONS = [
  {
    id: 1,
    translationKey: 'bookAppointment',
    href: 'book-appointment',
    color: 'bg-tertiary',
    textColor: 'text-white',
    pool: 'consultation' as QuotaPool,
  },
  {
    id: 2,
    translationKey: 'secondOpinion',
    href: 'second-opinion',
    color: 'bg-chart-2',
    textColor: 'text-white',
    pool: 'consultation' as QuotaPool,
  },
  {
    id: 3,
    translationKey: 'homecare',
    href: 'homecare',
    color: 'bg-chart-3',
    textColor: 'text-white',
    pool: 'none' as QuotaPool,
  },
  {
    id: 4,
    translationKey: 'printing',
    href: 'printing',
    color: 'bg-chart-4',
    textColor: 'text-white',
    requiresB2B: true,
    pool: 'printing' as QuotaPool,
  },
] as const;

function isPoolExhausted(
  pool: QuotaPool,
  b2bProfile: GetCurrentUser['b2bProfile'] | null | undefined,
): boolean {
  if (!b2bProfile) return false;
  if (pool === 'consultation') return b2bProfile.usageLimit <= 0;
  if (pool === 'printing') return (b2bProfile.printingUsageLimit ?? 0) <= 0;
  return false;
}

function quotaReachedToastKey(pool: QuotaPool): string {
  if (pool === 'printing') return 'home.printingQuotaReached';
  return 'home.consultationQuotaReached';
}

export default function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [specialty, setSpecialty] = useState<null | {
    id: string;
    name: string;
    description: string;
  }>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  const { data, isLoading, isFetching } = useData('GET /specialities', {
    pageSize: searchQuery ? 100 : 6,
    page: 1,
    query: debouncedSearchQuery || undefined,
  });

  const { data: reservations, isLoading: isLoadingReservations } = useData(
    'GET /patients/me/reservations',
    {
      page: 1,
      pageSize: 4,
    },
  );

  const { data: secondOpinions, isLoading: isLoadingSecondOpinions } = useData(
    'GET /patients/me/second-opinions',
    {
      page: 1,
      pageSize: 4,
    },
  );

  const homecareEnabled = useSetting('homecareEnabled');
  const isHomecareEnabled = homecareEnabled?.value === 'true';

  const {
    data: homecareReservations,
    isLoading: isLoadingHomecareReservations,
  } = useData(
    'GET /patients/me/homecare-reservations',
    {
      page: 1,
      pageSize: 4,
    },
    { enabled: isHomecareEnabled },
  );

  const { data: identity } = useUser();
  const isB2B = !!identity?.b2bProfile;

  const { data: printingRequests, isLoading: isLoadingPrintingRequests } =
    useData(
      'GET /patients/me/printing-requests',
      {
        page: 1,
        pageSize: 4,
      },
      { enabled: isB2B },
    );
  const appointmentFee = useSetting('appointmentFee');
  const consultationDisabled = isPoolExhausted(
    'consultation',
    identity?.b2bProfile,
  );

  if (
    isLoading ||
    isLoadingReservations ||
    isLoadingSecondOpinions ||
    isLoadingHomecareReservations ||
    isLoadingPrintingRequests
  )
    return <Loading />;

  return (
    <>
      <Header>
        <SearchComponent
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('home.searchSpecialties')}
        />
        <ProfileCard />
      </Header>
      <Dialog
        open={specialty !== null}
        onOpenChange={(isOpen) => !isOpen && setSpecialty(null)}
      >
        <div className="min-h-screen px-4">
          <div className="max-w-6xl">
            <div className="mb-8">
              <h1 className="mb-2 text-2xl font-semibold text-gray-900">
                {t('home.quickOptions')}
              </h1>
            </div>

            <div className="mb-12 grid gap-6 md:grid-cols-3">
              {OPTIONS.filter((option) => {
                if (option.translationKey === 'homecare' && !isHomecareEnabled)
                  return false;
                if ('requiresB2B' in option && !identity?.b2bProfile)
                  return false;
                return true;
              }).map((option) => {
                const disabled = isPoolExhausted(
                  option.pool,
                  identity?.b2bProfile,
                );
                return (
                  <Card
                    key={option.id}
                    className={`${option.color} ${option.textColor} border-none shadow-lg transition-all duration-300 ${
                      disabled
                        ? 'cursor-not-allowed opacity-60 grayscale'
                        : 'cursor-pointer hover:scale-105 hover:shadow-xl'
                    }`}
                    onClick={() => {
                      if (disabled) {
                        toast.error(t(quotaReachedToastKey(option.pool)));
                        return;
                      }
                      navigate(option.href);
                    }}
                  >
                    <CardContent className="px-6">
                      <div className="flex-1">
                        <h3 className="mb-2 text-xl font-semibold">
                          {t(`home.options.${option.translationKey}.title`)}
                        </h3>
                        <p className="mb-4 text-sm leading-relaxed opacity-90">
                          {t(`home.options.${option.translationKey}.subtitle`)}
                        </p>
                        <p className="text-sm leading-relaxed opacity-90">
                          {t(
                            `home.options.${option.translationKey}.description`,
                          )}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="mb-8 space-y-6">
              <HomeSectionHeader
                title={t('home.sections.upcomingAppointments')}
                navigateTo="/appointments"
              />
              {reservations?.records.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                  {t('home.sections.noUpcomingAppointments')}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {reservations?.records.map((appointment) => (
                    <AppointmentCard
                      key={appointment.id}
                      reservation={appointment}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="mb-8 space-y-6">
              <HomeSectionHeader
                title={t('home.sections.secondOpinionRequests')}
                navigateTo="/appointments?tab=second_opinion"
              />
              {secondOpinions?.records.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                  {t('home.sections.noSecondOpinionRequests')}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {secondOpinions?.records.map((record) => (
                    <SecondOpinionCard key={record.id} record={record} />
                  ))}
                </div>
              )}
            </div>

            {isHomecareEnabled && (
              <div className="mb-8 space-y-6">
                <HomeSectionHeader
                  title={t('home.sections.homecareReservations')}
                  navigateTo="/appointments?tab=homecare"
                />
                {homecareReservations?.records.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    {t('home.sections.noHomecareReservations')}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {homecareReservations?.records.map((reservation) => (
                      <HomecareReservationCard
                        key={reservation.id}
                        reservation={reservation}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {isB2B && (
              <div className="mb-8 space-y-6">
                <HomeSectionHeader
                  title={t('home.sections.printingRequests')}
                  navigateTo="/appointments?tab=printing"
                />
                {printingRequests?.records.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    {t('home.sections.noPrintingRequests')}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {printingRequests?.records.map((record) => (
                      <PrintingRequestCard key={record.id} record={record} />
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mb-8">
              <div className="mb-6 flex w-full items-center justify-between gap-2">
                <h2 className="text-xl font-semibold text-gray-900">
                  {searchQuery
                    ? t('home.searchResults')
                    : t('home.specialties')}
                </h2>
                {!searchQuery && (
                  <button
                    onClick={() => navigate('specialties')}
                    className="text-primary flex cursor-pointer items-center gap-1 text-sm hover:underline"
                  >
                    <span>{t('home.viewAll')}</span>
                    <ChevronLeft className="h-4 w-4 ltr:rotate-180" />
                  </button>
                )}
              </div>

              {searchQuery && (
                <p className="mb-4 text-sm text-gray-600">
                  {t('home.searchResultsFor', {
                    value: data?.records.length ?? 0,
                    query: searchQuery,
                  })}
                </p>
              )}

              {isLoading && <Loading />}

              {data?.records.length === 0 && !isFetching ? (
                <div className="col-span-full py-12 text-center">
                  <div className="mx-auto w-fit rounded-full bg-gray-100 p-4">
                    <StethoscopeIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">
                    {t('home.noSpecialties')}
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    {searchQuery
                      ? t('home.noSpecialtiesMatching', { query: searchQuery })
                      : t('home.noSpecialtiesDescription')}
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-3">
                  {data?.records.map((specialty) => (
                    <SpecialtyCard
                      key={specialty.id}
                      specialty={specialty}
                      onClick={() => {
                        const display = getLocalizedSpeciality(
                          specialty,
                          language,
                        );
                        setSpecialty({
                          id: specialty.id,
                          name: display.name,
                          description: display.description,
                        });
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{specialty?.name}</DialogTitle>
            <DialogDescription>{specialty?.description}</DialogDescription>
          </DialogHeader>

          {identity?.userType !== 'B2B' && (
            <PriceLine value={appointmentFee?.value} />
          )}

          <DialogFooter className="mx-auto flex items-center justify-center">
            <Button
              className="bg-tertiary px-4 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={consultationDisabled}
              onClick={() =>
                navigate(`book-appointment/${specialty?.id}/symptom-checker`)
              }
            >
              {t('home.dialog.bookAppointment')}
            </Button>
            <Button
              className="bg-chart-2 px-4 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={consultationDisabled}
              onClick={() =>
                navigate(`second-opinion/${specialty?.id}/symptom-checker`)
              }
            >
              {t('home.dialog.secondOpinion')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
